import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../../../../api/useAdd";
import useUpdate from "../../../../../api/useUpdate";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  license_number: "",
  license_expiry_date: "",
  emergency_contact: "",
  experience_years: "",
  status: "active",
  notes: "",
  photo: null,
};

const DriverForm = ({ driver, onSuccess, token }) => {
  const isEdit = !!driver;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const { t } = useTranslation();

  const { handleAdd, loading: addLoading, resetForm } = useAdd("drivers", token);
  const { handleUpdate, loading: updateLoading } = useUpdate("drivers", token, null);
  const loading = addLoading || updateLoading;

  useEffect(() => {
    if (driver) {
      setForm({
        name: driver.name || "",
        email: driver.email || "",
        phone: driver.phone || "",
        address: driver.address || "",
        license_number: driver.license_number || "",
        license_expiry_date: driver.license_expiry_date || "",
        emergency_contact: driver.emergency_contact || "",
        experience_years: driver.experience_years ?? "",
        status: driver.status || "active",
        notes: driver.notes || "",
        photo: null,
      });
      setPhotoPreview(driver.photo || null);
    } else {
      setForm(emptyForm);
      setPhotoPreview(null);
    }
    setErrors({});
  }, [driver]);

  useEffect(() => {
    if (resetForm) onSuccess();
  }, [resetForm]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("hr.nameRequired");
    if (!form.phone.trim()) e.phone = t("hr.phoneRequired");
    if (!form.license_number.trim()) e.license_number = t("hr.licenseNumberRequired");
    if (!form.license_expiry_date) e.license_expiry_date = t("hr.licenseExpiryRequired");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("hr.validEmailRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, photo: file }));
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== "") fd.append(k, v);
    });
    return fd;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const fd = buildFormData();
    if (isEdit) {
      handleUpdate(driver.id, fd);
      setTimeout(() => onSuccess(), 1000);
    } else {
      handleAdd(fd);
    }
  };

  const textField = (label, name, type = "text", extra = {}) => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        name={name}
        value={form[name]}
        onChange={handleChange}
        type={type}
        fullWidth
        size="small"
        variant="outlined"
        error={!!errors[name]}
        helperText={errors[name]}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
        {...extra}
      />
    </Grid>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {textField(`${t("hr.name")} *`, "name")}
        {textField(`${t("hr.phone")} *`, "phone")}
        {textField(t("hr.email"), "email", "email")}
        {textField(`${t("hr.licenseNo")} *`, "license_number")}
        {textField(`${t("hr.expiryDate")} *`, "license_expiry_date", "date")}
        {textField(t("hr.experienceYears"), "experience_years", "number")}
        {textField(t("hr.emergencyContact"), "emergency_contact")}

        <Grid item xs={12} sm={6}>
          <TextField select label={t("hr.status")} name="status" value={form.status} onChange={handleChange} fullWidth size="small">
            <MenuItem value="active">{t("hr.statusActive", "Active")}</MenuItem>
            <MenuItem value="inactive">{t("hr.statusInactive", "Inactive")}</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField label={t("hr.address")} name="address" value={form.address} onChange={handleChange} fullWidth size="small" multiline rows={2} />
        </Grid>

        <Grid item xs={12}>
          <TextField label={t("hr.notes")} name="notes" value={form.notes} onChange={handleChange} fullWidth size="small" multiline rows={2} />
        </Grid>

        <Grid item xs={12}>
          <Button variant="outlined" component="label" size="small">
            {t("hr.uploadPhoto")}
            <input type="file" hidden accept="image/*" onChange={handlePhoto} />
          </Button>
          {photoPreview && (
            <Box mt={1}>
              <img src={photoPreview} alt="preview" style={{ height: 80, borderRadius: 8, objectFit: "cover" }} />
            </Box>
          )}
          {form.photo && (
            <Typography variant="caption" display="block" mt={0.5}>
              {form.photo.name}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onSuccess} disabled={loading}>
          {t("hr.cancel")}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : null}>
          {isEdit ? t("hr.updateDriver") : t("hr.addDriverButton")}
        </Button>
      </Box>
    </Box>
  );
};

export default DriverForm;
